"use client";

import * as React from "react";
import { Filter, Plus, X } from "lucide-react";
import {
  Button,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@aio/platform-ui";
import type { ColumnDef } from "../columns";
import {
  getOperatorById,
  type ColumnWithCustomOperators,
  type OperatorDef,
} from "./operators";
import { FilterInput, type ActiveFilter } from "./filter-input";

/**
 * Props for {@link FilterBar}.
 */
export interface FilterBarProps<TRow> {
  /**
   * Column definitions for every filterable field. Non-filterable columns are
   * filtered out at the FilterInput level — they're still passed through here
   * so the bar can resolve column labels for chips.
   */
  columns: Array<ColumnDef<TRow> & ColumnWithCustomOperators>;
  /** Active filters (controlled). Empty array means "no filters". */
  filters: ActiveFilter[];
  /**
   * Notified when the active-filter list changes (chip add / remove / clear).
   * The bar is fully controlled — there is no internal copy of `filters`.
   */
  onChange: (next: ActiveFilter[]) => void;
  /** Override the trigger button label. Defaults to "Add filter". */
  addLabel?: string;
  /** Override the Clear all action label. Defaults to "Clear all". */
  clearAllLabel?: string;
  /** Extra classes for the wrapping element. */
  className?: string;
}

/**
 * Look up the column for a chip. Returns `undefined` if the filter references
 * a column that no longer exists (e.g. removed at runtime); the chip in that
 * case still renders but with the raw key as a fallback label so the user
 * can find and clear it.
 */
function findColumn<TRow>(
  columns: Array<ColumnDef<TRow> & ColumnWithCustomOperators>,
  key: string,
): (ColumnDef<TRow> & ColumnWithCustomOperators) | undefined {
  return columns.find((c) => c.key === key);
}

/**
 * Resolve the operator definition for a chip. Falls back to looking through
 * the column's `customOperators` for `type: "custom"` columns; returns
 * `undefined` when nothing matches.
 */
function findOperator<TRow>(
  column: (ColumnDef<TRow> & ColumnWithCustomOperators) | undefined,
  filter: ActiveFilter,
): OperatorDef | undefined {
  if (!column) return undefined;
  if (column.type === "custom") {
    const list = column.customOperators ?? [];
    return list.find((op) => op.id === filter.operatorId);
  }
  return getOperatorById(column.type, filter.operatorId);
}

/**
 * Render the human-readable value for a chip. Translates the polymorphic
 * `ActiveFilter.value` into a short string per the operator's value shape.
 *
 * - `null`  → "" (operator has no value, e.g. isEmpty)
 * - string  → as-is
 * - [a, b]  → "a — b"  (em-dash separator)
 * - string[]→ "a, b, c" (comma-joined)
 */
function renderValue(filter: ActiveFilter): string {
  const v = filter.value;
  if (v === null) return "";
  if (typeof v === "string") return v;
  if (Array.isArray(v)) {
    if (v.length === 2 && typeof v[0] === "string" && typeof v[1] === "string") {
      // Range — collapse empty ends to keep the chip readable. We do NOT
      // hide the chip when both ends are empty because the user explicitly
      // submitted it; the data-table normaliser is responsible for treating
      // a fully-empty range as "no constraint".
      const low = v[0];
      const high = v[1];
      if (low && high) return `${low} — ${high}`;
      if (low) return `≥ ${low}`;
      if (high) return `≤ ${high}`;
      return "";
    }
    // Multi — comma-join for the chip.
    return (v as string[]).join(", ");
  }
  return "";
}

/**
 * Filter bar that renders above the table.
 *
 * Layout (left → right, wrap on overflow):
 *
 *   [ Filter icon "Filters" ] [ chip 1 ] [ chip 2 ] ... [ Add filter ] [ Clear all ]
 *
 * Behaviour:
 * - Fully controlled: caller owns the `filters` array and is notified via
 *   `onChange` whenever a chip is added, removed, or the list is cleared.
 * - Adding a filter: clicking "Add filter" opens a Popover containing a
 *   {@link FilterInput}. Submitting the input appends to `filters` and
 *   closes the popover. The draft is reset when the popover closes.
 * - Removing a filter: each chip exposes an X button with an aria-label of
 *   "Remove {column} filter".
 * - Clear all: shown only when at least one filter is active.
 */
export function FilterBar<TRow>({
  columns,
  filters,
  onChange,
  addLabel,
  clearAllLabel,
  className,
}: FilterBarProps<TRow>): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<ActiveFilter | null>(null);

  // Reset the draft whenever the popover closes — opening it again starts
  // from scratch. We do this with an effect rather than inline so the
  // FilterInput's `value` prop stays controlled while the popover unmounts.
  React.useEffect(() => {
    if (!open) setDraft(null);
  }, [open]);

  const handleSubmit = React.useCallback(
    (filter: ActiveFilter) => {
      onChange([...filters, filter]);
      setDraft(null);
      setOpen(false);
    },
    [filters, onChange],
  );

  const handleRemove = React.useCallback(
    (key: string) => {
      // Remove only the first chip matching this key. Multiple chips on the
      // same column are allowed (e.g. "total > 100" + "total < 1000"); the
      // remove handler accepts an index for that case.
      const idx = filters.findIndex((f) => f.columnKey === key);
      if (idx === -1) return;
      const next = filters.filter((_, i) => i !== idx);
      onChange(next);
    },
    [filters, onChange],
  );

  const handleRemoveByIndex = React.useCallback(
    (index: number) => {
      const next = filters.filter((_, i) => i !== index);
      onChange(next);
    },
    [filters, onChange],
  );

  const handleClearAll = React.useCallback(() => {
    onChange([]);
  }, [onChange]);

  return (
    <div
      role="region"
      aria-label="Filters"
      className={cn(
        "flex flex-wrap items-center gap-2 border-b border-border bg-bg-elevated px-3 py-2",
        className,
      )}
      data-testid="filter-bar"
    >
      <span
        className="inline-flex items-center gap-1.5 text-sm text-fg-muted"
        aria-hidden="true"
      >
        <Filter className="size-4" />
        Filters
      </span>

      {/* Active chips */}
      {filters.map((filter, index) => {
        const column = findColumn(columns, filter.columnKey);
        const operator = findOperator(column, filter);
        const columnLabel = column?.header ?? filter.columnKey;
        const operatorLabel = operator?.label ?? filter.operatorId;
        const valueLabel = renderValue(filter);
        const showValue = operator?.requiresValue !== false && valueLabel;

        // Multiple chips on the same column key need stable distinct
        // testids; combine the key with the index for that.
        const removeTestId =
          filters.findIndex((f) => f.columnKey === filter.columnKey) === index
            ? `filter-bar-chip-remove-${filter.columnKey}`
            : `filter-bar-chip-remove-${filter.columnKey}-${index}`;

        return (
          <span
            key={`${filter.columnKey}-${index}`}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border border-border bg-bg px-2.5 py-1 text-xs",
              "text-fg",
            )}
            data-testid="filter-bar-chip"
          >
            <span className="font-medium">{columnLabel}</span>
            <span className="text-fg-muted">{operatorLabel}</span>
            {showValue ? (
              <span className="font-mono text-fg" data-testid="filter-bar-chip-value">
                {valueLabel}
              </span>
            ) : null}
            <button
              type="button"
              onClick={() =>
                // Prefer index-based removal when there are duplicates on the
                // same column; the helper returns the same shape either way.
                filters.findIndex((f) => f.columnKey === filter.columnKey) ===
                index
                  ? handleRemove(filter.columnKey)
                  : handleRemoveByIndex(index)
              }
              aria-label={`Remove ${columnLabel} filter`}
              className={cn(
                "ml-0.5 inline-flex size-4 items-center justify-center rounded-full",
                "text-fg-muted hover:bg-muted hover:text-fg",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              )}
              data-testid={removeTestId}
            >
              <X className="size-3" aria-hidden="true" />
            </button>
          </span>
        );
      })}

      {/* Add filter — popover hosting FilterInput */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            data-testid="filter-bar-add"
          >
            <Plus className="size-3.5" aria-hidden="true" />
            <span>{addLabel ?? "Add filter"}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto min-w-[28rem] p-3">
          <FilterInput
            columns={columns}
            value={draft}
            onChange={setDraft}
            onSubmit={handleSubmit}
          />
        </PopoverContent>
      </Popover>

      {filters.length > 0 ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearAll}
          className="ml-auto text-fg-muted"
          data-testid="filter-bar-clear-all"
        >
          {clearAllLabel ?? "Clear all"}
        </Button>
      ) : null}
    </div>
  );
}
